/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  PdfAnnotationFlagName,
  PdfAnnotationName,
  PdfAnnotationObject,
  PdfAnnotationSubtype,
  PdfTextAnnoObject,
  uuidV4,
} from '@embedpdf/models';
import { AnnotationPlugin } from '@embedpdf/snippet';
import { Observable } from 'rxjs';
import { isReply } from './pdf-comment-threads.util';

type AnnotationPatch = Partial<PdfAnnotationObject>;

/**
 * The private plugin methods every change goes through, whichever part of the viewer asks for it: the comment pane,
 * the selection menu, the keyboard, grouping. A test checks they still exist after an EmbedPDF upgrade.
 */
export interface AnnotationPluginInternals {
  createAnnotation(pageIndex: number, annotation: PdfAnnotationObject, ctx?: unknown, documentId?: string, options?: unknown): void;
  updateAnnotation(pageIndex: number, id: string, patch: AnnotationPatch, documentId?: string): void;
  updateAnnotationsMethod(patches: { pageIndex: number; id: string; patch: AnnotationPatch }[], documentId?: string): void;
  deleteAnnotation(pageIndex: number, id: string, documentId?: string): void;
}

/**
 * Where the comment pane's replies go when they are feedback comments rather than annotations (assignments). The pane
 * only shows a reply once the server has it, under the comment's id, so a later edit or delete finds it.
 */
export interface PaneComments {
  /** Posts a comment about the mark, and emits the comment's id. */
  post(mark: PdfAnnotationObject, contents: string): Observable<string>;
  edit(commentId: string, contents: string): Observable<unknown>;
  delete(commentId: string): Observable<unknown>;
  /** Shown on the viewer's replies, as on what they draw. */
  author?: string;
  /** A write reached the server. */
  saved(): void;
  /** A write didn't, and the pane has dropped it. */
  failed(error: unknown): void;
}

export interface AnnotationWriteRules {
  /** Whether the viewer drew the annotation, or wrote the reply. */
  editable(id: string): boolean;
  /** Asked at each write: set where replies are feedback comments. */
  comments?(): PaneComments | undefined;
}

/** Stop moving, resizing and retyping a mark on the page. The comment pane ignores both. */
const LOCK_FLAGS: PdfAnnotationFlagName[] = ['locked', 'lockedContents'];

const guardedPlugins = new WeakSet<AnnotationPlugin>();

/** A colleague's annotation, flagged so the page won't let the viewer move, resize or retype it. */
export function withLockFlags(annotation: object): object {
  const flags = (annotation as { flags?: PdfAnnotationFlagName[] }).flags ?? [];
  return { ...annotation, flags: [...new Set([...flags, ...LOCK_FLAGS])] };
}

/**
 * Makes the plugin ignore changes to annotations the viewer didn't draw. The lock flags only reach the page: the
 * comment pane edits and deletes any comment, and the plugin's API is frozen, so the methods behind it are wrapped.
 * The server would ignore such a change anyway; this keeps the screen from showing one that can't be saved.
 *
 * The pane's first comment on a bare mark is written into the mark itself. On a colleague's mark it becomes a
 * reply instead, so the text isn't lost.
 *
 * Where replies are feedback comments, the pane's replies, and its edits and deletes of them, go to the comments API.
 * The viewer only sees the result: a reply added, changed or dropped once the server has it, outside the undo history.
 */
export function guardColleagueAnnotations(plugin: AnnotationPlugin, rules: AnnotationWriteRules): void {
  if (guardedPlugins.has(plugin)) return;
  guardedPlugins.add(plugin);

  const internals = plugin as unknown as AnnotationPluginInternals;
  const { createAnnotation, updateAnnotation, updateAnnotationsMethod, deleteAnnotation } = internals;
  const capabilityFor = (documentId?: string) => (documentId ? plugin.provides().forDocument(documentId) : plugin.provides());
  const { editable } = rules;

  const postReply = (comments: PaneComments, mark: PdfAnnotationObject, contents: string, documentId?: string) =>
    comments.post(mark, contents).subscribe({
      next: (commentId) => {
        const reply = { ...replyTo(mark, contents, commentId), ...(comments.author && { author: comments.author }) };
        capabilityFor(documentId).importAnnotations([{ annotation: reply }]);
        comments.saved();
      },
      error: (error: unknown) => comments.failed(error),
    });

  internals.createAnnotation = (pageIndex, annotation, ctx, documentId, options) => {
    const comments = rules.comments?.();
    if (!comments || !isReply(annotation)) {
      createAnnotation.call(plugin, pageIndex, annotation, ctx, documentId, options);
      return;
    }
    const mark = capabilityFor(documentId).getAnnotationById(annotation.inReplyToId)?.object;
    if (mark && annotation.contents?.trim()) postReply(comments, mark, annotation.contents, documentId);
  };
  internals.updateAnnotation = (pageIndex, id, patch, documentId) => {
    const comments = rules.comments?.();
    const capability = capabilityFor(documentId);
    const annotation = capability.getAnnotationById(id)?.object;
    if (comments && annotation && isReply(annotation)) {
      const contents = patch.contents;
      if (!editable(id) || !contents?.trim() || contents === annotation.contents) return;
      comments.edit(id, contents).subscribe({
        next: () => {
          capability.syncAnnotationObject(id, { contents, modified: new Date() });
          comments.saved();
        },
        error: (error: unknown) => comments.failed(error),
      });
      return;
    }
    if (editable(id)) {
      updateAnnotation.call(plugin, pageIndex, id, patch, documentId);
      return;
    }
    if (annotation && !annotation.contents && patch.contents?.trim()) {
      if (comments) postReply(comments, annotation, patch.contents, documentId);
      else capability.createAnnotation(pageIndex, replyTo(annotation, patch.contents));
    }
  };
  internals.updateAnnotationsMethod = (patches, documentId) => {
    const own = patches.filter(({ id }) => editable(id));
    if (own.length) updateAnnotationsMethod.call(plugin, own, documentId);
  };
  internals.deleteAnnotation = (pageIndex, id, documentId) => {
    const comments = rules.comments?.();
    const capability = capabilityFor(documentId);
    const annotation = capability.getAnnotationById(id)?.object;
    if (comments && annotation && isReply(annotation)) {
      if (!editable(id)) return;
      comments.delete(id).subscribe({
        next: () => {
          capability.purgeAnnotation(pageIndex, id);
          comments.saved();
        },
        error: (error: unknown) => comments.failed(error),
      });
      return;
    }
    if (editable(id)) deleteAnnotation.call(plugin, pageIndex, id, documentId);
  };
}

/** A reply as the comment pane makes one: a note at the mark's corner. */
function replyTo(mark: PdfAnnotationObject, contents: string, id = uuidV4()): PdfTextAnnoObject {
  const now = new Date();
  return {
    id,
    type: PdfAnnotationSubtype.TEXT,
    pageIndex: mark.pageIndex,
    rect: { origin: { ...mark.rect.origin }, size: { width: 24, height: 24 } },
    created: now,
    modified: now,
    contents,
    inReplyToId: mark.id,
    flags: ['noRotate', 'noZoom', 'print'],
    name: PdfAnnotationName.Comment,
  };
}
